import { useId } from "react";
import { Building2, ChevronRight, Sparkles, X } from "lucide-react";
import { useDrawerA11y } from "@/hooks/useDrawerA11y";

/**
 * D-134 W1 — «З ким зустріч?»: перший крок створення зустрічі поза контекстом клієнта.
 * Існуючий клієнт → holding-picker (не змінено). Потенційний клієнт → prospect-флоу.
 */
export function MeetingTargetModeDrawer({
  open,
  onClose,
  onExisting,
  onProspect,
}: {
  open: boolean;
  onClose: () => void;
  onExisting: () => void;
  onProspect: () => void;
}) {
  const panelRef = useDrawerA11y(open, onClose);
  const headingId = useId();

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} aria-hidden>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative flex h-full w-full flex-col bg-background shadow-2xl outline-none sm:w-[440px]"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-background px-5 py-4">
          <h3 id={headingId} className="text-base font-semibold">З ким зустріч?</h3>
          <button type="button" onClick={onClose} aria-label="Закрити" className="rounded-md p-1 text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-5">
          <Choice
            icon={<Building2 className="h-5 w-5 text-primary" aria-hidden />}
            title="Існуючий клієнт"
            subtitle="Клієнт уже є в CRM (з 1С). Оберіть зі списку."
            onClick={onExisting}
          />
          <Choice
            icon={<Sparkles className="h-5 w-5 text-primary" aria-hidden />}
            title="Потенційний клієнт"
            subtitle="Новий лід, якого ще немає в базі. Знайдіть або створіть."
            onClick={onProspect}
          />
        </div>
      </div>
    </div>
  );
}

function Choice({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-xl border bg-card px-4 py-3.5 text-left hover:border-primary/50 hover:bg-muted/40"
    >
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary/10">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs text-muted-foreground">{subtitle}</span>
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
    </button>
  );
}
